import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { MENU_OBJECT_TYPES, SAVE_ERROR_MESSAGE } from 'constants/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';
import { toAnalyticsEntityType, trackMenuAvailabilityToggled } from 'helpers/menuAnalytics';

import { Switch } from '@mantine/core';
import MenuInformation from 'components/MenuManagement/MenuInformation';
import { useHandleErrorNotification } from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';

/**
 * @param {Object} props
 * @param {Record<string, any>} [props.menu]
 * @param {(enabled: boolean) => void} [props.setMenuEnabled]
 */
export default function MenuEnabledSwitch({ menu, setMenuEnabled = () => {} }) {
  const MenuInfoContext = useContext(MenuInformation?.Context);
  const { updateCubohMenu } = useMenuManagement();
  const handleErrorNotification = useHandleErrorNotification();
  const [enabled, setEnabled] = useState(menu.enabled);

  const { merchantId, key, itemType } = menu;

  const handleSave = async event => {
    try {
      setEnabled(event);
      setMenuEnabled(event);
      await updateCubohMenu({
        group: MenuInfoContext ? itemType : MENU_OBJECT_TYPES.single,
        id: key,
        data: { ...menu, enabled: event },
      });

      trackMenuAvailabilityToggled({
        entityType: toAnalyticsEntityType(MenuInfoContext ? itemType : MENU_OBJECT_TYPES.single),
        availability: event ? 'enabled' : 'disabled',
        menuId: menu.uuid,
        entityId: menu.uuid,
      });
    } catch (e) {
      Sentry.captureException(e);
      setEnabled(!event);
      setMenuEnabled(!event);
      handleErrorNotification({ customMessage: e.message || SAVE_ERROR_MESSAGE });
    } finally {
      if (MenuInfoContext?.sortedMenus) {
        // only run this if on MenuLanding with all menus showing
        const { sortedMenus, setSortedMenus } = MenuInfoContext;
        const updateSwitchFromSortedMenus = sortedMenus[merchantId].map(item => {
          if (item.key === key) {
            return { ...item, enabled: event };
          }
          return item;
        });
        setSortedMenus({
          ...sortedMenus,
          [merchantId]: updateSwitchFromSortedMenus,
        });
      }
    }
  };

  return (
    <Switch
      size="sm"
      checked={enabled}
      onChange={event => handleSave(event.currentTarget.checked)}
    />
  );
}

MenuEnabledSwitch.propTypes = {
  menu: PropTypes.shape({
    enabled: PropTypes.bool,
    itemType: PropTypes.string,
    key: PropTypes.string,
    merchantId: PropTypes.number,
  }),
  setMenuEnabled: PropTypes.func,
};
